<template>
  <Modal
    open
    fullscreen
    :title="props.title"
    @update:open="(value) => !value && saveProgressAndClose()"
  >
    <template #content="{ close }">
      <div class="flex h-full flex-col">
        <div
          class="flex items-center justify-between gap-lg border-b border-line px-2xl py-lg"
        >
          <p class="truncate text-heading-sm">{{ props.title }}</p>
          <IconButton
            :icon-name="IconName.CLOSE"
            :label="t('common.close')"
            :size="ButtonSize.SM"
            :variant="ButtonVariant.TEXT"
            :color="ButtonColor.SURFACE_CONTAINER"
            :disabled="isClosing"
            @click="close"
          />
        </div>
        <iframe
          ref="courseFrame"
          :src="props.courseUrl"
          :title="props.title"
          allow="autoplay; fullscreen"
          class="min-h-0 w-full flex-1 border-0"
          data-testid="scorm-iframe"
        />
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  ButtonColor,
  ButtonSize,
  ButtonVariant,
  IconButton,
  IconName,
  Modal,
} from '@exfluential/ui';

type Props = {
  title: string;
  courseUrl: string;
};

const props = defineProps<Props>();

const emit = defineEmits<{
  close: [];
}>();

const { t } = useI18n();

const SCORM_FLUSH_REQUEST_MESSAGE = 'exfluential:scorm-flush';
const SCORM_FLUSH_RESPONSE_MESSAGE = 'exfluential:scorm-flushed';
const SAVE_TIMEOUT_MS = 3000;

const courseFrame = ref<HTMLIFrameElement | null>(null);
const isClosing = ref(false);

const requestProgressSave = (courseWindow: Window) =>
  new Promise<void>((resolve) => {
    const requestId = `${Date.now()}-${Math.random()}`;
    const listener = new AbortController();
    const finish = () => {
      listener.abort();
      resolve();
    };

    window.addEventListener(
      'message',
      (event: MessageEvent) => {
        if (
          event.source === courseWindow &&
          event.data?.type === SCORM_FLUSH_RESPONSE_MESSAGE &&
          event.data?.requestId === requestId
        ) {
          finish();
        }
      },
      { signal: listener.signal },
    );

    const timeoutId = setTimeout(finish, SAVE_TIMEOUT_MS);
    listener.signal.addEventListener('abort', () => clearTimeout(timeoutId));

    try {
      courseWindow.postMessage(
        { type: SCORM_FLUSH_REQUEST_MESSAGE, requestId },
        new URL(props.courseUrl, globalThis.location.href).origin,
      );
    } catch {
      finish();
    }
  });

const saveProgressAndClose = async () => {
  if (isClosing.value) return;
  isClosing.value = true;

  const courseWindow = courseFrame.value?.contentWindow;
  if (courseWindow) {
    await requestProgressSave(courseWindow);
  }

  emit('close');
};
</script>
